import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useOrganization } from "@/auth/organization";
import { Button, ErrorAlert, useToast, type ButtonProps } from "@/components/ui";
import { portal } from "@/lib/api";
import type { Site } from "@/lib/types";

/** Brings a site that is scheduled for deletion back, and refreshes every list that showed it as scheduled. */
export function useRestoreSite(site: Site) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => portal.undeleteSite(site.id),
    onSuccess: async () => {
      toast.show({
        tone: "success",
        title: `${site.name} is back`,
        body: "The website is restored and the deletion is cancelled.",
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["sites", orgId] }),
        qc.invalidateQueries({ queryKey: ["hosting"] }),
        qc.invalidateQueries({ queryKey: ["hosted-site", site.id] }),
        qc.invalidateQueries({ queryKey: ["site", orgId, site.id] }),
        qc.invalidateQueries({ queryKey: ["overview"] }),
      ]);
    },
  });
}

/** "Restore" for a site scheduled for deletion; nothing for someone who may not delete (so not restore) it. */
export function RestoreSiteButton({
  site,
  variant = "secondary",
  size = "sm",
}: {
  site: Site;
} & Pick<ButtonProps, "variant" | "size">) {
  const { canOn } = useOrganization();
  const restore = useRestoreSite(site);
  if (!canOn("websites:delete", "site", site.id)) return null;
  return (
    <div className="flex flex-col gap-2">
      <Button variant={variant} size={size} loading={restore.isPending} onClick={() => restore.mutate()}>
        Restore
      </Button>
      <ErrorAlert error={restore.error} />
    </div>
  );
}
